import type { UiSnapshot } from '@prism/sim';

/**
 * The finale's two bars (playtest request).
 *
 * Prism's health only moves while its shield is down, so the two are not decoration —
 * they ARE the fight's clock. A player who cannot see the shield refill has no way to
 * know why their damage stopped landing, and one who cannot see the window closing has
 * no way to know how long to hold a burst for.
 *
 * All of it is read off the snapshot. The countdown in particular comes from the sim's
 * own deadline tick rather than a timer started here, because a UI running its own clock
 * drifts from the simulation the server replays and this window is short enough to show
 * it.
 */
export function BossBar({
  boss,
  tick,
  nameOf,
}: {
  boss: UiSnapshot['boss'];
  tick: number;
  /** Archetype id → display name. Passed in because this module must not import content,
   *  the same rule the renderer and the VFX layer follow. */
  nameOf: (archetype: string) => string;
}) {
  if (!boss) return null;

  const down = boss.downUntilTick > tick;
  const secondsLeft = down ? (boss.downUntilTick - tick) / 60 : 0;
  const hpPct = boss.maxHp > 0 ? Math.max(0, (boss.hp / boss.maxHp) * 100) : 0;
  const shieldPct =
    boss.maxShieldHp > 0 ? Math.max(0, (boss.shieldHp / boss.maxShieldHp) * 100) : 0;

  const state = boss.emerging ? 'EMERGING' : down ? 'EXPOSED' : 'SHIELDED';

  return (
    <div className={`bossbar bossbar--${state.toLowerCase()}`}>
      <div className="bossbar__head">
        <b className="bossbar__name">{nameOf(boss.archetype).toUpperCase()}</b>
        <span className="bossbar__state">
          {state}
          {down && ` · ${secondsLeft.toFixed(1)}s`}
        </span>
      </div>

      {/* Shield above health, in the order they have to be removed. An empty track is
          still drawn while it is down, so the bar that is about to come back does not
          vanish and take the explanation with it. */}
      <div className="bossbar__track bossbar__track--shield">
        <span className="bossbar__fill bossbar__fill--shield" style={{ width: `${shieldPct}%` }} />
      </div>
      <div className="bossbar__track bossbar__track--hp">
        <span className="bossbar__fill bossbar__fill--hp" style={{ width: `${hpPct}%` }} />
      </div>

      <div className="bossbar__foot">
        <span>{Math.max(0, Math.ceil(boss.hp)).toLocaleString()}</span>
        <span className="muted">
          {boss.emerging
            ? 'untouchable'
            : down
              ? 'health exposed — burst it'
              : `shield ${Math.max(0, Math.ceil(boss.shieldHp)).toLocaleString()}`}
        </span>
      </div>
    </div>
  );
}
